// Library imports
import React, { useCallback, useEffect, useState } from 'react';
import { Card, Divider, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

// Local imports
import Button from 'components/Common/Button';
import Tab from 'components/Common/Tab';
import MediaCard from 'components/Home/Card';
import colors from 'constants/colors';
import { useNavigate } from 'react-router-dom';
import routes from 'constants/routes';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';
import List from 'components/Common/List';
import MuiLoader from 'components/Common/Loader';
import { GIVING_BASKET_SUB_TYPE, PAYMENT_BASKET_SUB_TYPE } from 'utils/constants';
import { defaultHeaders } from 'appConfig';
import { fetchGivingBasket, resetGivingBaskets } from 'redux/slices/baskets/givingBasketSilce';
import { BasketHeaders } from 'types/baskets';
import { fetchPaymentBasket, resetPaymentBaskets } from 'redux/slices/baskets/paymentBasketSlice';
import LoadMoreButton from 'components/Common/LoadMoreButton';
import { selectSelectedParishId } from 'redux/selectors/parishContext';
import WalletSectionTabs from 'components/Wallet/WalletSectionTabs';

type BasketState = {
    activeRecords?: any;
    paymentList?: any;
    inActiveRecords: any;
    loading: boolean;
    error: string | null;
    loadMore: boolean;
};

// Styling
const FieldContainer = styled('div')({
    display: 'flex',
    justifyContent: 'flex-end',
    padding: 6,
    marginTop: 12,
});

const WalletBaskets = ({ basketType }: { basketType: 'paymentList' | 'givingList' }) => {
    const [tab, setTab] = useState('active');
    const [pageNumber, setPageNumber] = useState(0);
    const dispatch = useDispatch<AppDispatch>();
    const selectedParishId = useSelector(selectSelectedParishId);
    const { activeRecords, inActiveRecords, loading, loadMore }: BasketState = useSelector(
        (state: RootState) => state?.baskets[basketType]
    );
    const updateData = useCallback(
        (page: number) => {
            if (!selectedParishId) return;
            let basketHeaders: BasketHeaders = { status: 'ACTIVE', subType: GIVING_BASKET_SUB_TYPE };
            const headers = { ...defaultHeaders, pageNumber: page };
            if (tab === 'inActive') {
                basketHeaders.status = ['PENDING', 'CLOSED'];
            }
            if (basketType === 'paymentList') {
                basketHeaders.subType = PAYMENT_BASKET_SUB_TYPE;
                dispatch(fetchPaymentBasket({ ...basketHeaders, headers: { ...headers } }));
            } else {
                dispatch(fetchGivingBasket({ ...basketHeaders, headers: { ...headers } }));
            }
        },
        [dispatch, tab, basketType, selectedParishId]
    );

    useEffect(() => {
        setPageNumber(0);
        if (basketType === 'paymentList') {
            dispatch(resetPaymentBaskets());
        } else {
            dispatch(resetGivingBaskets());
        }
    }, [dispatch, selectedParishId, basketType]);

    useEffect(() => {
        updateData(pageNumber);
    }, [pageNumber, updateData, selectedParishId]);

    const setTabValue = (value: string) => {
        setPageNumber(0);
        setTab(value);
    };

    const navigate = useNavigate();

    const navigationHandler = (id: string | number | undefined) => {
        if (!id) {
            return;
        }
        navigate(routes.home.basketDetails.replace(':id', String(id)));
    };
    return (
        <Card sx={{ p: { xs: 2, lg: 6 }, mx: { xs: 2, lg: 24 }, my: 1 }}>
            {basketType === 'givingList' && <WalletSectionTabs current="giving" />}
            <Typography
                sx={{ fontSize: 16, fontWeight: 400, color: colors.darkblue, mt: basketType === 'givingList' ? 2 : 0 }}
                variant="h5"
            >
                {basketType === 'givingList' ? 'Giving' : 'Payment'} Baskets
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 400, color: colors.darkblue, marginBottom: 1 }} variant="h5">
                These balances do not include any pending transactions. As soon as a pending transaction is completed the total will be
                added to the available balance for that basket. To view pending transactions click into any of your baskets.
            </Typography>
            <Tab
                tabs={[
                    { value: 'active', label: 'Active' },
                    { value: 'inActive', label: 'Inactive' },
                ]}
                tabValue={tab}
                setTabValue={setTabValue}
            />
            <FieldContainer>
                <Button
                    sx={{ width: { xs: '50%', sm: '30%', lg: '20%' }, fontSize: { xs: 12, sm: 16 } }}
                    disabled={!selectedParishId}
                    onClick={() => navigate(routes.home.basketCreate)}
                    title="Create New"
                />
            </FieldContainer>
            <Stack position="relative" minHeight={200}>
                {tab === 'active' && (
                    <List data={loading ? [] : activeRecords} loading={loading}>
                        {(event, index) => <MediaCard key={index} onClickNavigation={navigationHandler} event={event} />}
                    </List>
                )}
                {tab === 'inActive' && (
                    <List data={loading ? [] : inActiveRecords} loading={loading}>
                        {(event, index) => <MediaCard key={index} onClickNavigation={navigationHandler} event={event} />}
                    </List>
                )}
                <MuiLoader loading={loading} absolute />
                {!loading && loadMore && <LoadMoreButton onClick={() => setPageNumber(pageNumber + 1)} />}
            </Stack>
        </Card>
    );
};

export default WalletBaskets;
